import { useMemo, useState } from "react";
import { message, Skeleton } from "antd5";
import { Flex } from "styles/utility-components";

import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import FilterDrawer from "components/filter_form/filter_drawer/FilterDrawer";
import FilterFormResults from "components/filter_form/filter_form_results/FilterFormResults";
import {
  useNoticeListsPageState,
  useSavedViews,
} from "components/notice_lists/useNoticeListsPageState";
import ViewsContainer from "components/notice_lists/views_bar/ViewsContainer";
import { EmptyNotices } from "components/notices/EmptyNotices";
import { ExportNoticesButton } from "components/notices/ExportNoticesButton";
import { FilterForm } from "components/notices/FilterForm";
import { DEFAULT_PAGINATION, NoticeTable } from "components/notices/NoticeTable";
import {
  ALL_NOTICE_COLUMNS,
  convertNoticeFiltersToSearchRequest,
  DEFAULT_NOTICE_SORT_ORDER,
  generateNoticeAnalysisUrl,
  getAppliedFilters,
  getNoticeColumnCopy,
  NoticeColumns,
  NoticeFilters,
} from "components/notices/utils";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import { FeatureType } from "components/paywall/paywallUtils";
import EmptyStateAction from "components/saved_searches/EmptyStateAction";
import FilterPanelToggle from "components/search_page/FilterPanelToggle";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import SidebarOld from "components/ui/sidebar/SidebarOld";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { createUseDebounce } from "lib/debounce";
import { useCreditsStatus } from "lib/hooks/api/credits/useCreditsStatus";
import { useNotices } from "lib/hooks/api/notices/useNotices";
import { useAllViews } from "lib/hooks/api/notices/views/useAllViews";
import { StandardViewName } from "lib/hooks/api/notices/views/useStandardViewTarget";
import { useLogSearch } from "lib/hooks/api/userActivity/useLogSearch";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import { useUnsavedChangeWarning } from "lib/hooks/useUnsavedChangesWarning";
import { parseBooleanValue, useURLState } from "lib/hooks/useURLState";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { LoggedEntityType, SavedViewDataType } from "lib/types/graphQLEnums";

import css from "./NoticeListsPage.module.scss";

const useDebounce = createUseDebounce(300);

export function NoticeListsPage() {
  const {
    filters,
    setFilters,
    selectedView,
    setSelectedView,
    isLoadingViews,
    tableColumns,
    setTableColumns,
    hasUserModifiedView,
    filterCount,
  } = useNoticeListsPageState();
  const { data: allViews } = useAllViews(SavedViewDataType.Notice);
  const { recentViews } = useSavedViews();

  useUnsavedChangeWarning(hasUserModifiedView);

  const [, messageContext] = message.useMessage();
  const { openPaywallModal } = usePaywall();

  const [activeGroups, setActiveGroups] = useState<string[]>([]);
  const [selectedNotices, setSelectedNotices] = useState<string[]>();
  const [openFilterDrawer] = useURLState<boolean | undefined>(
    "openFilterDrawer",
    false,
    parseBooleanValue,
  );
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(!!openFilterDrawer);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const hydratedRecentViews = recentViews.map((id: string) => allViews?.find((v) => v.id === id));

  const subscription = useSubscription();

  /**
   * We need to override applied legacy filters
   * Only set supplierMentionType if supplierGuids are set
   */
  const appliedFilters = useMemo((): NoticeFilters => {
    filters.supplierMentionType =
      filters.supplierGuids && filters.supplierGuids.length > 0
        ? filters.supplierMentionType
        : undefined;

    // clear signals filter as this is removed
    filters.signals = [];

    return filters;
  }, [filters]);

  const paywallFeature = useMemo((): FeatureType | undefined => {
    if (selectedView?.isStandardView) {
      switch (selectedView.name) {
        case StandardViewName.Competitor:
          return subscription.hasDataTypes("BUYERS|SUPPLIERS") ? undefined : "COMPETITORS";
        case StandardViewName.Partner:
          return subscription.hasDataTypes("BUYERS|SUPPLIERS") ? undefined : "PARTNERS";
        case StandardViewName.CompetitorPartner:
          return subscription.hasDataTypes("BUYERS|SUPPLIERS") ? undefined : "SUPPLIERS";

        default:
          return undefined;
      }
    }
    return undefined;
  }, [selectedView?.isStandardView, selectedView?.name, subscription]);

  const { logEvent } = useTracking();

  const debouncedSetFilters = useDebounce((filters: NoticeFilters) => {
    setFilters(filters);
  });

  const user = useAssertCurrentUser();

  useCreditsStatus({ teamId: user.team.id });

  const { isLoading, data: noticeData } = useNotices(
    convertNoticeFiltersToSearchRequest(appliedFilters, DEFAULT_PAGINATION),
    false,
    { enabled: Boolean(selectedView) },
  );
  useLogSearch(LoggedEntityType.Notice, selectedView ? appliedFilters : undefined);

  const totalResults = noticeData?.pagingInfo?.totalResults;

  const hasMarketAnalysisAccess = subscription.hasDataTypes("MARKET_ANALYSIS");
  const handleMarketAnalysisClick = (evt: React.MouseEvent, id: string | undefined) => {
    if (!hasMarketAnalysisAccess) {
      evt.preventDefault();
      openPaywallModal("STRATEGY", {
        requiredDataSubscription: "MARKET_ANALYSIS",
        strategyPaywall: true,
      });
    } else {
      logEvent(EventNames.marketAnalysisCreated, {
        viewId: id ?? "N/A",
      });
    }
  };

  const renderFilterBar = () => {
    return (
      <div className={css.filterBar}>
        <div className={css.filterItems}>
          <Flex column gap={16}>
            <Flex alignItems="center" gap={8}>
              <FilterPanelToggle
                open={isSidebarOpen}
                onOpenChange={setIsSidebarOpen}
                appliedFiltersCount={getAppliedFilters(filters).length}
              />
              <Button
                as="a"
                href={generateNoticeAnalysisUrl(filters, selectedView?.id, hasUserModifiedView)}
                icon="barChart12"
                buttonType="secondary"
                onClick={(e) => handleMarketAnalysisClick(e, selectedView?.id)}
              >
                Analyse
                {!hasMarketAnalysisAccess && <UIcon icon={"lock01"} size={14} />}
              </Button>
            </Flex>

            <FilterFormResults
              totalResults={totalResults || 0}
              sortedByField={getNoticeColumnCopy(
                filters.sort?.field || DEFAULT_NOTICE_SORT_ORDER.field,
              )}
              isLoading={isLoading}
            />
          </Flex>
        </div>
        {filterCount > 0 && (
          <div className={css.actionContainer}>
            <div className={css.clearSelected}>
              {selectedNotices && selectedNotices.length > 0 && (
                <ClearSelectedButton
                  count={selectedNotices.length}
                  onClear={() => setSelectedNotices([])}
                />
              )}
            </div>
            {selectedNotices && selectedNotices.length === 0 && (
              <EditColumnsButton
                selectedColumns={tableColumns}
                onNewColumnSettingChange={(columns) =>
                  setTableColumns(columns.map((c) => c.field) as NoticeColumns[])
                }
                allAvailableColumns={ALL_NOTICE_COLUMNS}
                dataType={EventDataTypes.record}
              />
            )}
            <ExportNoticesButton filters={filters} selectedNotices={selectedNotices} />
          </div>
        )}
      </div>
    );
  };

  const emptyStateMessage = {
    title:
      hydratedRecentViews?.length > 1 ? "Recent searches" : "Apply filters to start your search",
    description:
      hydratedRecentViews?.length > 1 ? undefined : "Or try our templates to help you get started",
  };

  return (
    <DetailsPage>
      <DetailsHeader borderBottom={!paywallFeature}>
        <ViewsContainer />
      </DetailsHeader>
      <>
        {paywallFeature ? (
          <PaywallBanner
            featureType={paywallFeature}
            className={paywallFeature === "AWARDS" ? undefined : css.fullHeightBanner}
          />
        ) : (
          <DetailsContent className={css.content}>
            {isLoadingViews ? (
              <>
                <Skeleton.Input active />
                <Skeleton active />
              </>
            ) : (
              <>
                {filterCount > 0 ? (
                  <div className={css.tableContentArea}>
                    <SidebarOld open={isSidebarOpen} offsetTop={73}>
                      <Flex column>
                        <FilterForm
                          resetKey={selectedView?.id}
                          onChange={debouncedSetFilters}
                          filters={filters}
                          activeGroups={activeGroups}
                          onActiveGroupsChange={setActiveGroups}
                          onClose={() => setIsFilterDrawerOpen(false)}
                          isSidebar
                          isLoadingResults={isLoading}
                          isEmptyState={!selectedView && filterCount === 0}
                          totalResults={totalResults}
                          setSelectedView={setSelectedView}
                          onSideBarOpenChange={setIsSidebarOpen}
                        />
                      </Flex>
                    </SidebarOld>
                    <div className={css.tableMainArea}>
                      <div className={css.filterBarContainer}>{renderFilterBar()} </div>
                      <NoticeTable
                        filters={filters}
                        columnSettings={tableColumns.map((c) => c.field)}
                        onSortChange={(sort) => setFilters({ ...filters, sort })}
                        selectedRows={selectedNotices}
                        onSelectedRowsChange={(rows) => setSelectedNotices(rows)}
                        emptyText={<EmptyNotices setIsSidebarOpen={setIsSidebarOpen} />}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={css.tableContentArea}>
                    <SidebarOld open={isSidebarOpen} offsetTop={109}>
                      <Flex column>
                        <FilterForm
                          resetKey={selectedView?.id}
                          onChange={debouncedSetFilters}
                          filters={filters}
                          activeGroups={activeGroups}
                          onActiveGroupsChange={setActiveGroups}
                          onClose={() => setIsFilterDrawerOpen(false)}
                          isSidebar
                          isLoadingResults={isLoading}
                          totalResults={totalResults}
                          isEmptyState={!selectedView && filterCount === 0}
                          setSelectedView={setSelectedView}
                        />
                      </Flex>
                    </SidebarOld>
                    <div className={css.tableMainArea}>
                      <EmptyState
                        type="startingState"
                        heading={emptyStateMessage.title}
                        description={emptyStateMessage.description}
                        size="large"
                        width="100%"
                        action={
                          <EmptyStateAction
                            hydratedRecentViews={hydratedRecentViews}
                            setSelectedView={setSelectedView}
                            dataType={"notice"}
                          />
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </DetailsContent>
        )}
      </>
      {/* This is for the awards preview */}
      {paywallFeature === "AWARDS" && (
        <DetailsContent className={css.previewContent}>
          <NoticeTable
            filters={filters}
            columnSettings={tableColumns.map((c) => c.field)}
            onSortChange={(sort) => setFilters({ ...filters, sort })}
            isPreview
          />
        </DetailsContent>
      )}

      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <FilterForm
            onClose={() => setIsFilterDrawerOpen(false)}
            onChange={debouncedSetFilters}
            filters={filters}
            activeGroups={activeGroups}
            onActiveGroupsChange={setActiveGroups}
            setSelectedView={setSelectedView}
          />
        )}
      </FilterDrawer>
      {messageContext}
    </DetailsPage>
  );
}
